import { useEffect, useRef, useState } from "react";

/**
 * IntersectionObserver-based reveal-on-scroll wrapper.
 * Adds a "visible" class when the element enters the viewport,
 * driving the CSS transition in index.css (.reveal / .reveal.visible).
 *
 * Props:
 *   as: element tag ("div" default)
 *   variant: "up" | "left" | "right" | "fade" | "scale"
 *   delay: ms delay applied via inline style
 *   once: if true (default), only reveals the first time
 */
export default function Reveal({
  as: Tag = "div",
  variant = "up",
  delay = 0,
  once = true,
  className = "",
  style,
  children,
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Fallback: if IntersectionObserver isn't supported, just show it
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [once]);

  return (
    <Tag
      ref={ref}
      className={`reveal reveal-${variant} ${visible ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
